<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import KanbanAPI from 'dashboard/api/kanban';
import KanbanCardTemplatesAPI from 'dashboard/api/kanbanCardTemplates';

const props = defineProps({
  item: { type: Object, required: true },
  open: { type: Boolean, default: false },
  variant: { type: String, default: 'menu' },
});
const emit = defineEmits(['updated']);
const { t } = useI18n();
const templates = ref([]);
const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);

const load = async () => {
  if (loaded.value || loading.value) return;
  loading.value = true;
  try {
    const { data } = await KanbanCardTemplatesAPI.list();
    templates.value = data.templates || [];
    loaded.value = true;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.CARD_TEMPLATES.LOAD_ERROR'),
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.open,
  open => {
    if (open) load();
  },
  { immediate: true }
);

const selectTemplate = async event => {
  const value = event.target.value;
  saving.value = true;
  try {
    const { data } = await KanbanAPI.updateItem(props.item.id, {
      kanban_card_template_id: value ? Number(value) : null,
    });
    Object.assign(props.item, data);
    emit('updated', data);
  } catch (error) {
    event.target.value = props.item.kanban_card_template_id || '';
    emitter.emit('newToastMessage', {
      message: t('KANBAN.CARD_TEMPLATES.SELECT_ERROR'),
      type: 'error',
    });
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <label
    v-show="open"
    :class="
      variant === 'toolbar'
        ? 'inline-flex items-center gap-2 text-xs font-medium text-n-slate-11'
        : 'block border-y border-n-weak px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-n-slate-10'
    "
    @click.stop
  >
    {{ t('KANBAN.CARD_TEMPLATES.SELECT_LABEL') }}
    <select
      class="h-8 rounded-md border border-n-weak bg-n-solid-1 px-2 text-xs normal-case text-n-slate-12"
      :class="variant === 'toolbar' ? 'w-44' : 'mt-1 w-full'"
      :value="item.kanban_card_template_id || ''"
      :disabled="loading || saving"
      @change="selectTemplate"
    >
      <option value="">{{ t('KANBAN.CARD_TEMPLATES.LEGACY') }}</option>
      <option
        v-for="template in templates"
        :key="template.id"
        :value="template.id"
      >
        {{ template.name }}
      </option>
    </select>
  </label>
</template>
